import type { ApiProgressNotificationInfo, ApiWorkStatusNotification } from '@notifications/api'
import type { DeleteElementEvent, GltfLoadError } from '@notifications/types'
import { nanoid } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { hasKeysOfUnknown, isObject, throwIfNever } from 'common/helpers'
import { messengerRef } from 'common/messenger'
import type {
  BaseObject,
  ElementStates,
  GeomateNotification,
  NotificationStatus,
  PickerProgressEvent,
  PolygonUploadResult,
  ProjectElementInfo,
  ProjectState,
  WorkStatus,
} from 'common/types'

const DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT = 5

export const getDummyNotification = (): GeomateNotification => ({
  id: nanoid(),
  message: 'Заглушка сообщения (что-то пошло не так)',
  status: 'normal',
  taskId: nanoid(),
})

export function constructSimpleNotification(
  notification: Partial<GeomateNotification>,
): GeomateNotification {
  return { ...getDummyNotification(), ...notification }
}

function getStatusFromElementState(state: ElementStates): NotificationStatus {
  switch (state) {
    case 'ElementDeleted':
      return 'system'
  }
}

export function constructEmptyPointsetCalculationNotification(
  calculationName: string,
): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: `Нет пересечений множеств/объектов: ${calculationName}`,
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function constructSwapWorkflowItemsErrorNotification(): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message:
      'Заменяемый объект приводит к появлению циклических ссылок, выберите другой объект для замены',
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function constructCopyVarigramDataErrorNotification(): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: 'Проведите вариограммый анализ, сейчас указаны значения по умолчанию',
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function constructOrchCalculationErrorNotification(
  calculationName: string,
): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: `Ошибка в расчете ${calculationName}`,
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function constructDeleteRelatedWorkflowItemErrorNotification(): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: 'Удаление объекта, используемого в других операциях, невозможно',
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function convertDeleteElementEventToNotification({
  elementId,
  elementName,
  deleteElementCb,
}: DeleteElementEvent): GeomateNotification {
  return {
    id: nanoid(),
    taskId: elementId,
    message: `Объект ${elementName} будет удален из проекта`,
    status: getStatusFromElementState('ElementDeleted'),
    showProgress: 'timer',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
    notificationCloseCb: deleteElementCb,
    actions: [{ label: 'Отмена', onClick: 'undoDelete' }],
  }
}

const getGltfErrorMessage = (entityType: BaseObject, entityName: string): string => {
  switch (entityType) {
    case 'well':
      return `Отсутствует инклинометрия для сквжины ${entityName}`
    default:
      return `Ошибка загрузки ${entityName}`
  }
}

export function convertEntityGltfLoadErrorToNotification({
  elementName,
  elementType,
}: GltfLoadError): GeomateNotification {
  return {
    id: nanoid(),
    message: getGltfErrorMessage(elementType, elementName),
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function convertPublishedElementToNotification(
  element: ProjectElementInfo,
): GeomateNotification {
  return {
    id: nanoid(),
    taskId: element.uid,
    message: `${element.name} теперь доступен всем участникам проекта`,
    status: 'success',
    showProgress: 'timer',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

export function convertFileUploadErrorToNotification(
  progress: ApiProgressNotificationInfo,
): GeomateNotification {
  const message =
    progress.status === 'Failed'
      ? `При загрузке в ${progress.name} произошла ошибка`
      : progress.message
  return {
    id: progress.entityId,
    taskId: progress.taskId,
    message,
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
    status: getStatusFromFileUploadStatus(progress.status),
  }
}

function getStatusFromFileUploadStatus(
  status: ApiProgressNotificationInfo['status'],
): NotificationStatus {
  switch (status) {
    case 'Done':
      return 'success'
    case 'Failed':
      return 'alert'
    case 'Running':
    case 'Pending':
      return 'normal'
  }
}

export function convertPolygonUploadResultToNotification(
  polygonResult: PolygonUploadResult,
): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: {
      type: 'polygon',
      label: `Полигон ${polygonResult.fileName} ${polygonResult.isSuccess ? 'загружен' : 'не загружен'}`,
      caption: polygonResult.failedPolylinesMessage,
    },
    status: polygonResult.isSuccess ? 'success' : 'alert',
    autoClose: polygonResult.isSuccess ? DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT : undefined,
  }
}

export function convertMlNotifyStatusToNotification(
  mlNotifyStatus: ApiWorkStatusNotification,
): GeomateNotification {
  const isCalculationFailed = mlNotifyStatus.status === 'Faulted'

  return {
    id: nanoid(),
    taskId: mlNotifyStatus.uid,
    message: (isCalculationFailed ? mlNotifyStatus.errorText : mlNotifyStatus.description) ?? '',
    status: getStatusFromMLStatus(mlNotifyStatus.status),
  }
}

function getStatusFromMLStatus(status: WorkStatus): NotificationStatus {
  switch (status) {
    case 'Running':
    case 'WaitingToRun':
    case 'Canceled':
    case 'Unknown':
      return 'normal'
    case 'RanToCompletion':
      return 'success'
    case 'Faulted':
      return 'alert'
  }
}

export function convertMLErrorToNotification(err: unknown): GeomateNotification {
  let message = 'Произошла ошибка расчета ML'

  if (err instanceof AxiosError) {
    const data: unknown = err.response?.data

    if (!!data && typeof data === 'object' && 'detail' in data && typeof data.detail === 'string') {
      message = data.detail
    }
  }

  return { id: nanoid(), message, status: 'alert' }
}

export function convertVSPErrorToNotification(err: unknown): GeomateNotification {
  let message = 'Произошла ошибка в VSP'
  if (err instanceof AxiosError) {
    const data: unknown = err.response?.data
    if (!!data && typeof data === 'string') {
      message = data
    }
  }
  return { id: nanoid(), message, status: 'alert' }
}

export function convertSurvey2dFileUploadErrorToNotification(err: unknown): GeomateNotification {
  let message = 'Произошла ошибка при загрузке 2D съемки'
  if (err instanceof AxiosError) {
    const data: unknown = err.response?.data
    const code = err.response?.status
    if (
      isObject(data) &&
      hasKeysOfUnknown(data, 'title') &&
      typeof data.title === 'string' &&
      code !== 500
    ) {
      message = data.title
    }
  }
  return { id: nanoid(), message, status: 'alert' }
}

export function convertPickerProgressToNotification(
  progress: PickerProgressEvent,
): GeomateNotification {
  const ids = { id: nanoid(), taskId: progress.taskId }
  const progressIds = { id: progress.entityId, taskId: progress.taskId }

  switch (progress.status) {
    case 'Progress':
      return {
        ...progressIds,
        status: 'normal',
        message: `Автопрослеживание горизонта: ${progress.progress}%`,
        autoClose: progress.progress === 100 ? 1 : undefined,
      }
    case 'Failed':
      return {
        ...ids,
        status: 'alert',
        message: 'Автопрослеживание горизонта не удалось',
      }
    case 'Aborted':
      return {
        ...ids,
        status: 'warning',
        message: 'Автопрослеживание горизонта прервано',
      }
    case 'Done':
      messengerRef.current?.send({
        type: 'updateHorizonInterpretation',
        payload: progress.entityId,
      })
      return {
        ...ids,
        status: 'success',
        message: 'Автопрослеживание горизонта успешно завершилось',
        autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
      }
    case 'Pending':
      return {
        ...ids,
        status: 'normal',
        message: 'Автопрослеживание поставлено в очередь',
        autoClose: 1,
      }
    default:
      throwIfNever(progress.status)
  }
}

export function constructVspAppliedNotification(vspName: string): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: `Скоростной закон ${vspName} применен`,
    status: 'system',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
    showProgress: 'timer',
  }
}

export function constructProjectStateChangedNotification(
  newState: ProjectState,
): GeomateNotification {
  return {
    id: nanoid(),
    message: getMessageFromNewProjectState(newState),
    status: 'system',
    taskId: nanoid(),
    showProgress: 'timer',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}

function getMessageFromNewProjectState(newState: ProjectState): string {
  switch (newState) {
    case 'Private':
      return 'Проект снят с публикации'
    case 'Public':
      return 'Проект успешно опубликован. Его могут просматривать и редактировать все участники Геомейта'
  }
}

export function constructDeletedToTrashElementAddErrorNotification(
  entityName: string,
): GeomateNotification {
  return {
    id: nanoid(),
    taskId: nanoid(),
    message: `Объект ${entityName} находится в корзине`,
    status: 'alert',
    autoClose: DEFAULT_NOTIFICATION_AUTOCLOSE_TIMEOUT,
  }
}
